import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../environments/environment';
import { RolesApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';
import { LoginModalComponent } from '../login-modal/login-modal.component';

export interface FooterLink {
  label: string;
  url: string;
  external?: boolean;
}

export interface SocialLink {
  label: string;
  url: string;
  iconSrc: string;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule, LoginModalComponent],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent {
  private rolesService = inject(RolesApiService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private keycloak = inject(KeycloakService);

  currentYear = new Date().getFullYear();
  /** Official NHA site (footer “Website” link). */
  readonly publicWebsiteUrl = 'https://nha.gov.in/';
  email = environment.email;

  importantLinks: FooterLink[] = [
    { label: 'Dashboard', url: '', external: false },
    { label: 'Discussion', url: '/discussions', external: false },
    { label: 'Challenge', url: '/challenges', external: false },
  ];

  developerLinks: FooterLink[] = [
    { label: 'Control Plane API docs', url: environment.controlPlaneApiDocsUrl, external: true },
    { label: 'File Server API docs', url: environment.fileServerApiDocsUrl, external: true },
    { label: 'Resource Server API docs', url: environment.resourceServerApiDocsUrl, external: true },
    // { label: 'OGC Server API docs', url: environment.ogcServerApiDocsUrl, external: true },
    // { label: 'Sandbox Server API docs', url: environment.sandboxServerApiDocsUrl, external: true }
  ];

  // Icon paths from public folder
  globalIcon = '/global.svg';
  emailIcon = '/sms.svg';

  showLoginModal = false;
  contactUsRedirectUri =
    typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}/contact-us`
      : '/contact-us';

  /** Order: Facebook, Instagram, LinkedIn, X, YouTube (official Ayushman / NHA channels). */
  socialLinks: SocialLink[] = [
    {
      label: 'Facebook',
      url: 'https://www.facebook.com/AyushmanNHA/',
      iconSrc: '/facebook_symbol.svg.svg',
    },
    {
      label: 'Instagram',
      url: 'https://www.instagram.com/ayushmannha/',
      iconSrc: '/social-instagram.svg',
    },
    {
      label: 'LinkedIn',
      url: 'https://www.linkedin.com/company/ayushmanbharatgoi/',
      iconSrc: '/linkedin_symbol.svg.svg',
    },
    {
      label: 'X',
      url: 'https://x.com/AyushmanNHA',
      iconSrc: '/social-x.svg',
    },
    {
      label: 'YouTube',
      url: 'https://www.youtube.com/channel/UCkd7w2rww0HQB4lZ-l3dB6g',
      iconSrc: '/youtube_symbol.svg.svg',
    },
  ];

  /**
   * Check if user is logged in
   */
  private async checkLoginStatus(): Promise<boolean> {
    try {
      return await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  /**
   * Handle click on Dashboard: navigate to the panel for the user's highest role.
   * Requires login; fetches roles and navigates to /admin/home, /org-admin/home, /provider/home, or /consumer/home.
   */
  async onDashboardClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      const isLoggedIn = await this.checkLoginStatus();
      if (!isLoggedIn) {
        this.toasterService.warning("You must be logged in to access the dashboard.");
        return;
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const route = this.rolesService.getHighestRolePanelRoute(userRoles || []);
      this.router.navigate([route]);
    } catch (error) {
      console.error('Error navigating to dashboard:', error);
      this.toasterService.warning("Unable to open dashboard. Please try again.");
    }
  }

  /**
   * Handle click on protected panel links (e.g. Canvas Playground)
   * Check if user is logged in and show toaster message if not
   */
  async onProtectedPanelClick(event: Event, linkLabel: string): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      const isLoggedIn = await this.checkLoginStatus();
      if (!isLoggedIn) {
        this.toasterService.warning("You must be logged in, in order to access the panels");
        return;
      }

      const link = this.importantLinks.find(l => l.label === linkLabel);
      if (link?.url) {
        this.router.navigate([link.url]);
      }
    } catch (error) {
      console.error('Error checking user authentication:', error);
      const link = this.importantLinks.find(l => l.label === linkLabel);
      if (link?.url) {
        this.router.navigate([link.url]);
      }
    }
  }

  async onContactUsClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      const isLoggedIn = await this.checkLoginStatus();
      if (isLoggedIn) {
        this.router.navigate(['/contact-us']);
        return;
      }
      this.showLoginModal = true;
    } catch (error) {
      console.error('Error opening contact us with auth check:', error);
      this.showLoginModal = true;
    }
  }
}


